/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable max-lines */
import { useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "sonner"

import type { FactCheckMode, FactCheckResult } from "@/api/fact-check-api"

import { ChevronDown, Loader2, ShieldCheck, X } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useIndexedFilesQuery } from "@/features/ai-explorer/hooks/use-indexed-files-query"
import { useBaseSettings } from "@/features/base-settings/hooks/use-base-settings"
import { FactCheckPanel } from "@/features/chat/components/fact-check-panel"
import { FactCheckJobManager } from "@/features/chat/utils/fact-check-job-manager"
import { useWebSearchEnabled } from "@/hooks/use-route-overrides"
import { useSessionId } from "@/hooks/use-session-id"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"
import { useNavigatorStore } from "@/stores/navigator-store"
import { useFactCheckErrorMessage } from "@/utils/fact-check-error-handler"

interface FactCheckButtonProps {
  messageId?: string
  answerText?: string
  language?: string
  sessionType: string
  userQuery?: string
  citations?: Array<{
    id: string
    title: string
    kind: "doc" | "datasource" | "url" | "file" | "library"
  }>
  initialResult?: FactCheckResult | null
  sessionId?: string
  files?: File[] // Freshly uploaded files (not yet indexed)
}

export function FactCheckButton({
  messageId,
  answerText,
  language,
  sessionType,
  userQuery,
  citations = [],
  initialResult = null,
  sessionId: propSessionId,
  files,
}: FactCheckButtonProps) {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [mode, setMode] = useState<FactCheckMode>("standard")
  const [result, setResult] = useState<FactCheckResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  const [statusMessage, setStatusMessage] = useState<string>(t("fact_check.checking"))
  const [showSourceSelector, setShowSourceSelector] = useState(false)
  const [selectedSources, setSelectedSources] = useState<string[]>([])
  const jobIdRef = useRef<string | null>(null)
  const isMountedRef = useRef(true)
  const lastAutoOpenFromUrlKeyRef = useRef<string | null>(null)

  // Get session ID from hook or prop
  const hookSessionId = useSessionId()
  const sessionId = propSessionId || hookSessionId

  // Get settings service for token
  const settingsService = SettingsService.getInstance()

  // Get indexed files for this session
  const indexedFilesQuery = useIndexedFilesQuery({
    sessionId: sessionId ?? undefined,
    token: settingsService.token,
    enabled: Boolean(sessionId),
  })

  // Determine if session has indexed files
  const hasIndexedFiles = Boolean(indexedFilesQuery.data && indexedFilesQuery.data.length > 0)

  // Get current uploaded files from props
  const currentFiles = files

  // Get selected model from DB-backed settings
  const { gptModel: selectedModel } = useBaseSettings({ sessionType })

  // Get enabled libraries from navigator store
  const navigatorStore = useNavigatorStore(sessionType)
  const enabledLibraries = navigatorStore((state) => state.enabledLibraries)
  const canUseWebSource = useWebSearchEnabled()

  // Get localized error message
  const localizedErrorMessage = useFactCheckErrorMessage(result?.errorCode)

  useEffect(() => {
    // Re-arm on every mount: StrictMode (and any remount that keeps this
    // fiber) runs the cleanup below and then mounts again. Without this the
    // ref stays false, so progress and the finished result are dropped and
    // the button hangs on "Checking…" until the message is reopened (#3322).
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      jobIdRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      lastAutoOpenFromUrlKeyRef.current = null
    }
  }, [messageId])

  // Infer default sources from citations
  useEffect(() => {
    if (citations.length === 0) {
      setSelectedSources(canUseWebSource ? ["web"] : [])
      return
    }

    const inferredSources = new Set<string>()
    citations.forEach((citation) => {
      if (citation.kind === "url" && canUseWebSource) {
        inferredSources.add("web")
      } else if (citation.kind === "file") {
        inferredSources.add("files")
      } else if (citation.kind !== "url") {
        // "library", "doc", or "datasource"
        inferredSources.add("libraries")
      }
    })

    // Default to web if nothing inferred
    if (inferredSources.size === 0) {
      if (canUseWebSource) {
        inferredSources.add("web")
      }
    }

    setSelectedSources(Array.from(inferredSources))
  }, [canUseWebSource, citations])

  // Load persisted result and reset state when message changes
  useEffect(() => {
    // Reset state when switching messages
    if (initialResult) {
      setResult(initialResult)
      // Set mode from persisted result
      if (initialResult.mode) {
        setMode(initialResult.mode)
      }
    } else {
      // Clear state when switching to a message without fact-check result
      setResult(null)
      setMode("standard")
    }
    // Reset error state
    setError(null)
  }, [messageId, answerText, initialResult])

  // Auto-open panel when navigated via "View fact check" deep-link
  useEffect(() => {
    if (!messageId) return

    const params = new URLSearchParams(location.search)
    const targetMessageId = params.get("factCheckMessageId")
    if (!targetMessageId || targetMessageId !== messageId) return

    const autoOpenKey = `${targetMessageId}:${params.get("factCheckUpdatedAt") ?? ""}`
    if (lastAutoOpenFromUrlKeyRef.current === autoOpenKey) return

    if (!result && !initialResult) return

    setIsPanelOpen(true)
    lastAutoOpenFromUrlKeyRef.current = autoOpenKey

    // Clean up URL params so this doesn't auto-open repeatedly
    params.delete("factCheckMessageId")
    params.delete("factCheckUpdatedAt")
    const nextSearch = params.toString()
    void navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true }
    )
  }, [initialResult, location.pathname, location.search, messageId, navigate, result])

  const handleCancel = () => {
    if (jobIdRef.current) {
      FactCheckJobManager.getInstance().cancel(jobIdRef.current)
      jobIdRef.current = null
    }
    if (isMountedRef.current) {
      setIsLoading(false)
      setError(null)
    }
  }

  const handleFactCheck = async (
    selectedMode?: FactCheckMode,
    forceRerun: boolean = false,
    customSources?: string[]
  ) => {
    const checkMode = selectedMode || mode
    const sourcesToUse = (customSources || selectedSources).filter((source) =>
      canUseWebSource ? true : source !== "web"
    )

    // Validate: if libraries are selected, ensure at least one library is enabled
    if (sourcesToUse.includes("libraries")) {
      const enabledLibraryIds = Object.entries(enabledLibraries)
        .filter(([, library]) => library.checked)
        .map(([libraryId]) => libraryId)

      if (enabledLibraryIds.length === 0) {
        toast.error(t("fact_check.library_source_required_title"), {
          description: t("fact_check.library_source_required_message"),
        })
        return
      }
    }

    // Check if mode actually changed (compare with result.mode from backend, not local state)
    const modeChanged = result && result.mode && checkMode !== result.mode

    // If result exists, mode is the same, and not forcing rerun/changing sources/mode, just open panel
    if (result && !modeChanged && !forceRerun && !customSources) {
      setIsPanelOpen(true)
      return
    }

    setIsLoading(true)
    setError(null)
    setMode(checkMode)
    setResult(null) // Clear previous result if mode changed or forcing rerun
    setShowSourceSelector(false) // Close source selector

    // Get enabled library IDs for context
    const enabledLibraryIds = Object.entries(enabledLibraries)
      .filter(([, library]) => library.checked)
      .map(([libraryId]) => libraryId)

    try {
      const manager = FactCheckJobManager.getInstance()
      const { jobId, promise } = manager.start({
        sessionType,
        sessionId: sessionId || undefined,
        messageId,
        request: {
          messageId,
          answerText,
          language: language || i18n.language,
          userQuery,
          citations,
          mode: checkMode,
          forceRerun,
          sourceTypes: sourcesToUse.length > 0 ? sourcesToUse : undefined,
          sessionId: sessionId || undefined,
          hasIndexedFiles, // Indicates session has files in search index
          files: currentFiles, // Freshly uploaded files (not yet indexed)
          context: {
            overrides: {
              custom_gpt_model: selectedModel?.id ?? "auto",
              search_services: enabledLibraryIds,
              route_id: sessionType,
            },
          },
        },
        onActivity: (message) => {
          if (!isMountedRef.current) return
          setStatusMessage(message)
        },
      })
      jobIdRef.current = jobId

      const factCheckResult = await promise
      if (!isMountedRef.current) {
        return
      }
      setResult(factCheckResult)
      setIsPanelOpen(true)
    } catch (err) {
      // Ignore abort errors
      if (err instanceof Error && (err.name === "CanceledError" || err.name === "AbortError")) {
        return
      }
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : "Failed to fact check")
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false)
        jobIdRef.current = null
      }
    }
  }

  const toggleSource = (source: string) => {
    setSelectedSources((prev) => {
      if (prev.includes(source)) {
        // Keep at least one source selected
        if (prev.length === 1) return prev
        return prev.filter((s) => s !== source)
      }
      return [...prev, source]
    })
  }

  const getConfidenceBadgeStyle = (confidence: number) => {
    if (confidence >= 80) {
      return "border-success bg-success/15 text-foreground"
    }
    if (confidence >= 60) {
      return "border-warning bg-warning/15 text-foreground"
    }
    if (confidence >= 40) {
      return "border-primary-ink/40 bg-primary/10 text-primary-ink "
    }
    return "border-destructive bg-destructive/15 text-foreground"
  }

  return (
    <div className="flex items-center gap-2">
      {!result && !isLoading && (
        <DropdownMenu open={showSourceSelector} onOpenChange={setShowSourceSelector}>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button className="gap-2" size="sm" variant="outline">
                  <ShieldCheck className="size-4" />
                  {t("fact_check.button_text")}
                  <ChevronDown className="size-3" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>{t("fact_check.button_tooltip")}</p>
            </TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="end" className="w-64">
            {/* Source Selection Section */}
            <div className="px-2 py-2" onPointerDown={(e) => e.stopPropagation()}>
              <div className="text-muted-foreground mb-2 text-xs font-semibold">
                {t("fact_check.select_sources", { defaultValue: "Select Sources" })}
              </div>
              <div className="space-y-2">
                {canUseWebSource && (
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={selectedSources.includes("web")}
                      data-testid="fact-check-web-source"
                      onCheckedChange={() => toggleSource("web")}
                    />
                    <span>{t("fact_check.source_web", { defaultValue: "Web" })}</span>
                  </label>
                )}
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox
                    checked={selectedSources.includes("files")}
                    data-testid="fact-check-files-source"
                    onCheckedChange={() => toggleSource("files")}
                  />
                  <span>{t("fact_check.source_files", { defaultValue: "Session Files" })}</span>
                </label>
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <Checkbox
                    checked={selectedSources.includes("libraries")}
                    data-testid="fact-check-libraries-source"
                    onCheckedChange={() => toggleSource("libraries")}
                  />
                  <span>{t("fact_check.source_libraries", { defaultValue: "Libraries" })}</span>
                </label>
              </div>
              <div className="text-muted-foreground mt-3 text-xs">
                {selectedSources.length === 0 && "Select at least one source"}
                {selectedSources.length > 0 &&
                  `${selectedSources.length} source${selectedSources.length > 1 ? "s" : ""} selected`}
              </div>
            </div>

            <DropdownMenuSeparator />

            {/* Mode Selection Section */}
            <DropdownMenuItem
              data-testid="fact-check-mode-quick"
              onClick={() => handleFactCheck("quick")}
            >
              <div className="flex flex-col">
                <span className="font-medium">{t("fact_check.mode_quick")}</span>
                <span className="text-muted-foreground text-xs">
                  {t("fact_check.mode_quick_desc")}
                </span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="fact-check-mode-standard"
              onClick={() => handleFactCheck("standard")}
            >
              <div className="flex flex-col">
                <span className="font-medium">{t("fact_check.mode_standard")}</span>
                <span className="text-muted-foreground text-xs">
                  {t("fact_check.mode_standard_desc")}
                </span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="fact-check-mode-thorough"
              onClick={() => handleFactCheck("thorough")}
            >
              <div className="flex flex-col">
                <span className="font-medium">{t("fact_check.mode_thorough")}</span>
                <span className="text-muted-foreground text-xs">
                  {t("fact_check.mode_thorough_desc")}
                </span>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {!result && isLoading && (
        <div className="flex items-center gap-1">
          <Button disabled className="gap-2" size="sm" variant="outline">
            <Loader2 className="size-4 animate-spin" />
            {statusMessage}
          </Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button className="px-2" size="sm" variant="outline" onClick={handleCancel}>
                <X className="size-4" />
                <span className="sr-only">{t("fact_check.cancel")}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>{t("fact_check.cancel")}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      )}

      {result && (
        <div className="flex items-center gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                aria-label={t("fact_check.confidence_label", { score: result.confidence })}
                className="flex items-center gap-2 rounded-md transition-opacity hover:opacity-80"
                onClick={() => setIsPanelOpen(true)}
              >
                <Badge
                  className={cn("cursor-pointer gap-1", getConfidenceBadgeStyle(result.confidence))}
                  variant="outline"
                >
                  <ShieldCheck className="size-3" />
                  {t("fact_check.confidence_badge", { score: result.confidence })}
                  {result.mode && <span className="ml-1 text-xs opacity-60">({result.mode})</span>}
                </Badge>
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>{t("fact_check.click_to_see_details")}</p>
            </TooltipContent>
          </Tooltip>

          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button className="px-2" size="sm" variant="ghost">
                    <ChevronDown className="size-3" />
                    <span className="sr-only">{t("fact_check.rerun")}</span>
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>{t("fact_check.rerun")}</p>
              </TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="end" className="w-64">
              {/* Source Selection Section */}
              <div className="px-2 py-2" onPointerDown={(e) => e.stopPropagation()}>
                <div className="text-muted-foreground mb-2 text-xs font-semibold">
                  {t("fact_check.select_sources", { defaultValue: "Select Sources" })}
                </div>
                <div className="space-y-2">
                  {canUseWebSource && (
                    <label className="flex cursor-pointer items-center gap-2 text-sm">
                      <Checkbox
                        checked={selectedSources.includes("web")}
                        data-testid="fact-check-rerun-web-source"
                        onCheckedChange={() => toggleSource("web")}
                      />
                      <span>{t("fact_check.source_web", { defaultValue: "Web" })}</span>
                    </label>
                  )}
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={selectedSources.includes("files")}
                      data-testid="fact-check-rerun-files-source"
                      onCheckedChange={() => toggleSource("files")}
                    />
                    <span>{t("fact_check.source_files", { defaultValue: "Session Files" })}</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={selectedSources.includes("libraries")}
                      data-testid="fact-check-rerun-libraries-source"
                      onCheckedChange={() => toggleSource("libraries")}
                    />
                    <span>{t("fact_check.source_libraries", { defaultValue: "Libraries" })}</span>
                  </label>
                </div>
                <div className="text-muted-foreground mt-3 text-xs">
                  {selectedSources.length === 0 && "Select at least one source"}
                  {selectedSources.length > 0 &&
                    `${selectedSources.length} source${selectedSources.length > 1 ? "s" : ""} selected`}
                </div>
              </div>

              <DropdownMenuSeparator />

              {/* Mode Selection Section */}
              <DropdownMenuItem
                data-testid="fact-check-rerun-current"
                onClick={() => handleFactCheck(mode, true)}
              >
                <div className="flex flex-col">
                  <span className="font-medium">{t("fact_check.rerun")}</span>
                  <span className="text-muted-foreground text-xs">
                    {t("fact_check.rerun_current_mode", { mode })}
                  </span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="fact-check-rerun-quick"
                onClick={() => handleFactCheck("quick")}
              >
                <div className="flex flex-col">
                  <span className="font-medium">{t("fact_check.mode_quick")}</span>
                  <span className="text-muted-foreground text-xs">
                    {t("fact_check.mode_quick_desc")}
                  </span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="fact-check-rerun-standard"
                onClick={() => handleFactCheck("standard")}
              >
                <div className="flex flex-col">
                  <span className="font-medium">{t("fact_check.mode_standard")}</span>
                  <span className="text-muted-foreground text-xs">
                    {t("fact_check.mode_standard_desc")}
                  </span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="fact-check-rerun-thorough"
                onClick={() => handleFactCheck("thorough")}
              >
                <div className="flex flex-col">
                  <span className="font-medium">{t("fact_check.mode_thorough")}</span>
                  <span className="text-muted-foreground text-xs">
                    {t("fact_check.mode_thorough_desc")}
                  </span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {error && (
        <span className="text-destructive text-xs" role="alert">
          {error}
        </span>
      )}

      {localizedErrorMessage && !error && (
        <span className="text-destructive text-xs" role="alert">
          {localizedErrorMessage}
        </span>
      )}

      {result && (
        <FactCheckPanel
          isOpen={isPanelOpen}
          result={result}
          sessionId={sessionId || undefined}
          onClose={() => setIsPanelOpen(false)}
        />
      )}
    </div>
  )
}
